import {
    AfterViewInit,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    QueryList,
    ViewChild,
    ViewChildren,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { INameId } from '@modeso/types__aumico-lib-core-be';
import { UntilDestroy } from '@ngneat/until-destroy';
import { I18NextService } from 'angular-i18next';
import Debug from 'debug';
import { QuillEditorComponent, QuillModules } from 'ngx-quill';
import { DraggableMultiSelectMapper } from '../../mappers/aum-form/draggableMultiSelect.mapper';
import { KeyForPropertyPipe } from './../../pipes/KeyForPropertyPipe';
import { IFormDelegate } from './IFormDelegate.interface';
import { IFormOptions } from './IFormOptions.interface';
import { IProperty } from './IProperty.interface';
import { RadioFormControl } from './customFormFields/radio-form-control';
const debug = Debug('aumico:aumico-lib-core-fe:FormComponent');
@UntilDestroy()
@Component({
    selector: 'aumico-form',
    templateUrl: 'form.component.html',
    styleUrls: ['form.component.scss'],
})
export class FormComponent implements OnInit, AfterViewInit {
    OPTIONS: IFormOptions = {
        properties: [],
    };
    @Input() public set options(options: IFormOptions) {
        if (options) {
            this.OPTIONS = options;
            debug('set option');
        }
        this.setSubmitText();
        this.setMandatoryText();
        this.setInvalidText();
        this.setMinLengthErrorText();
    }

    DELEGATE?: IFormDelegate;
    @Input() public set delegate(delegate: IFormDelegate) {
        if (delegate) {
            this.DELEGATE = delegate;
        }
    }

    DATA?: any;
    @Input() public set data(data: any) {
        if (data) {
            this.DATA = data;
        }
    }

    @Output() formChange: EventEmitter<any> = new EventEmitter();
    @Output() isFormValid: EventEmitter<any> = new EventEmitter();

    @Input() disableSubmitButton = false;

    protected subscriptions = [];
    form: UntypedFormGroup;
    toolbarOptions: QuillModules;
    private translatableMarkupText: string[] = [];
    showDialogErrors = false;
    mandatoryText: string;
    invalidText: string;
    submitText: string;
    minLengthText: string;

    @ViewChild('singleEditor', { static: false }) singleEditor: QuillEditorComponent;
    @ViewChildren('multipleEditors') multipleEditors: QueryList<QuillEditorComponent>;

    constructor(
        private formBuilder: UntypedFormBuilder,
        private keyForProperty: KeyForPropertyPipe,
        private draggableMultiSelectMapper: DraggableMultiSelectMapper,
        private i18nextService: I18NextService,
    ) {}

    ngOnInit(): void {
        debug('ngOnInit');
        this.prepareToolbarOptions();
        this.updateForm();
        this.form.valueChanges.subscribe((change) => {
            const output = this.mapFormValueToOutputFormat(this.form.value);
            this.formChange.emit(output);
            this.isFormValid.emit(this.form.valid);
        });
    }

    submit() {
        debug(this.form.value);
        if (!this.form.valid) {
            debug('form is invalid');
            debug(this.form.errors);
            this.showDialogErrors = true;
            return;
        }
        if (this.DELEGATE) {
            this.showDialogErrors = false;
            const output = this.mapFormValueToOutputFormat(this.form.getRawValue());
            this.DELEGATE.onSubmit(output, this.OPTIONS);
        }
    }

    private mapFormValueToOutputFormat(value: any): any {
        const formProperties = this.getProperties();
        const output = {};
        formProperties.forEach((property: IProperty) => {
            if (property.type === 'translatable-markup' || property.type === 'translatable-text') {
                property.languages.forEach((lang) => {
                    const langKey = this.keyForProperty.transform(property, lang);
                    if (output[property.property] === undefined) {
                        output[property.property] = {};
                    }
                    output[property.property][lang] = value[langKey];
                });
            } else {
                output[property.property] = value[property.property];
            }
        });
        debug(output);
        return output;
    }

    public getProperties(): IProperty[] {
        if (this.OPTIONS && this.OPTIONS.properties) {
            return this.OPTIONS.properties;
        } else {
            this.OPTIONS.properties = [];
            return this.OPTIONS.properties;
        }
    }

    public compareObjects(o1: INameId, o2: INameId): boolean {
        return o1?.name === o2?.name && o1?.id === o2?.id;
    }

    private initFormGroup() {
        const properties = this.getProperties();

        const formgroup = {};
        const data = this.DATA || {};

        properties.forEach((property: IProperty) => {
            const key = property.property;
            const originalValue = data[key];

            if (property.type === 'translatable-markup' || property.type === 'translatable-text') {
                // giving it a default value
                const value = originalValue || ({} as { [key: string]: string });
                property.languages.forEach((lang) => {
                    const langKey = this.keyForProperty.transform(property, lang);
                    formgroup[langKey] = new UntypedFormControl(
                        {
                            value: value[lang],
                            disabled: property.disabled,
                        },
                        this.getValidators(property),
                    );
                    if (property.type === 'translatable-markup') {
                        this.translatableMarkupText.push(value[lang]);
                    }
                });
                data[key] = value;
            } else if (property.type === 'radio') {
                formgroup[property.property] = new RadioFormControl(
                    property.options,
                    this.compareObjects,
                    originalValue,
                    this.getValidators(property),
                );
            } else if (property.type === 'draggable-multi-select') {
                formgroup[property.property] = new UntypedFormControl(
                    {
                        value: this.draggableMultiSelectMapper.getSelectedOptionsOfSelectionOptions(
                            property.options,
                            originalValue,
                        ),
                        disabled: property.disabled,
                    },
                    this.getValidators(property),
                );
            } else {
                formgroup[property.property] = new UntypedFormControl(
                    { value: originalValue, disabled: property.disabled },
                    this.getValidators(property),
                );
            }
        });
        this.DATA = data;
        debug('formgroup');
        debug(formgroup);
        this.form = this.formBuilder.group(formgroup);
    }

    private getValidators(property: IProperty): ValidatorFn[] {
        return [...(property.extraValidators || []), ...(property.required ? [Validators.required] : [])];
    }

    private updateForm() {
        // Construct Form
        debug('updateForm');
        this.initFormGroup();
    }

    ngAfterViewInit(): void {
        this.appendTextToSingleEditor();
    }

    private appendTextToMultipleEditors() {
        debug(this.translatableMarkupText, 'this.translatableMarkupText');
        debug(this.multipleEditors, 'this.multipleEditors');

        this.multipleEditors.forEach((item, index) => {
            // debug(item);

            if (this.translatableMarkupText[index]) {
                const editorBody = item.quillEditor;

                const content = editorBody.clipboard.convert({ html: this.translatableMarkupText[index] });

                editorBody.setContents(content);
            }
        });
    }

    private appendTextToSingleEditor() {
        const properties = this.getProperties();

        debug(this.singleEditor, 'this.singleEditor');
        properties.forEach((property: IProperty) => {
            if (property.type === 'markup') {
                debug(this.DATA[property.property]);
                const editorBody = this.singleEditor.quillEditor;

                const content = editorBody.clipboard.convert(this.DATA[property.property]);

                editorBody.setContents(content);
            }
        });
    }

    private isFormHasTranslatableMarkup(): boolean {
        const properties = this.getProperties();

        for (const property of properties) {
            if (property.type === 'translatable-markup') {
                return true;
            }
        }

        return false;
    }

    public setSubmitText() {
        if (this.OPTIONS.submitText) {
            this.submitText = this.OPTIONS.submitText;
        } else {
            this.submitText = this.i18nextService.t('aumico_core_form_submit') as string;
        }
    }
    public setMandatoryText() {
        if (this.OPTIONS.mandatoryText) {
            this.mandatoryText = this.OPTIONS.mandatoryText;
        } else {
            this.mandatoryText = this.i18nextService.t('aumico_core_form_mandatory_error') as string;
        }
    }
    public setInvalidText() {
        if (this.OPTIONS.invalidText) {
            this.invalidText = this.OPTIONS.invalidText;
        } else {
            this.invalidText = this.i18nextService.t('aumico_core_form_invalid_error') as string;
        }
    }

    public async setMinLengthErrorText() {
        this.minLengthText = this.i18nextService.t('aumico_core_form_min_length_error') as string;
    }

    getMinimumLengthErrorText(length: number): string {
        return `${this.minLengthText} ${length}`;
    }

    getPropertyName(propertyName: string, lang: string): string {
        return `${propertyName}-${lang}`;
    }

    private prepareToolbarOptions() {
        this.toolbarOptions = {
            toolbar: [
                [{ font: ['Arial', 'serif', 'monospace'] }],
                [{ size: [] }],
                ['bold', 'italic', 'underline', { color: [] }, { background: [] }],
                [{ align: [] }, { list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
                ['clean', 'strike', { direction: 'rtl' }, 'blockquote'],
            ],
        };
    }
}
